import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Loader, ErrorState, PageHeader } from "../../components/common/States";
import ReasonDialog from "../../components/admin/ReasonDialog";
import { ROLE, fmtDate } from "../../lib/labels";
import { PLATFORM_ROLE, usePlatformCan } from "../../lib/platform";

const TYPES = { "": "Tous les comptes", public: "Comptes publics", collaborator: "Collaborateurs", internal: "Administrateurs internes" };

const MembershipChip = ({ m, canEdit, onEdit }) => (
  <span className="mr-1 mb-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5" data-testid={`admin-membership-${m.id}`}>
    {m.company} · {canEdit ? (
      <select className="bg-transparent text-xs" value={m.role} onChange={(e) => onEdit(m, { role: e.target.value })} data-testid={`admin-membership-role-${m.id}`}>
        {Object.entries(ROLE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>) : ROLE[m.role]}
    {m.status === "inactive" && <b className="text-rose-600">désactivé</b>}
    {canEdit && <button className="text-indigo-700 underline" onClick={() => onEdit(m, { status: m.status === "inactive" ? "active" : "inactive" })} data-testid={`admin-membership-status-${m.id}`}>{m.status === "inactive" ? "réactiver" : "désactiver"}</button>}
  </span>
);

const UserRow = ({ u, me, can, onToggle, onEdit }) => (
  <tr data-testid={`admin-user-row-${u.id}`}>
    <td className="px-4 py-3"><p className="font-medium">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
    <td className="px-4 py-3 text-xs">
      {u.platform_role && <span className="mr-1 rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-800">{PLATFORM_ROLE[u.platform_role]}</span>}
      {u.memberships.map((m) => <MembershipChip key={m.id} m={m} canEdit={can("users:manage")} onEdit={onEdit} />)}
      {!u.platform_role && !u.memberships.length && <span className="text-slate-500">Usager</span>}
    </td>
    <td className="px-4 py-3 text-xs">
      <span className={u.is_active === false ? "text-rose-600" : "text-emerald-700"} data-testid={`admin-user-status-${u.id}`}>{u.deleted_at ? "Supprimé" : u.is_active === false ? "Désactivé" : "Actif"}</span>
      {u.status_reason && <p className="text-slate-500">{u.status_reason}</p>}
    </td>
    <td className="px-4 py-3 text-slate-500">{fmtDate(u.created_at, false)}</td>
    <td className="px-4 py-3 text-right">
      {can("users:manage") && u.id !== me.id && !u.deleted_at && (!u.platform_role || can("admins:manage")) && (
        <Button size="sm" variant={u.is_active === false ? "default" : "outline"} onClick={() => onToggle(u)} data-testid={`admin-user-toggle-${u.id}`}>
          {u.is_active === false ? "Réactiver" : "Désactiver"}</Button>)}
    </td>
  </tr>
);

export default function AdminUsers() {
  const { user: me } = useAuth();
  const can = usePlatformCan();
  const [q, setQ] = useState("");
  const [f, setF] = useState({ q: "", type: "", status: "", page: 1 });
  const [pending, setPending] = useState(null);
  const { data, loading, error, reload } = useApi(`/admin/users?q=${encodeURIComponent(f.q)}&type=${f.type}&status=${f.status}&page=${f.page}`);

  const confirm = async (reason) => {
    try {
      if (pending.kind === "user") await api.patch(`/admin/users/${pending.u.id}`, { is_active: pending.u.is_active === false, reason });
      else await api.patch(`/admin/memberships/${pending.m.id}`, { ...pending.change, reason });
      toast.success("Modification enregistrée"); setPending(null); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onToggle = (u) => (u.is_active === false ? (async () => { try { await api.patch(`/admin/users/${u.id}`, { is_active: true }); toast.success("Compte réactivé"); reload(); } catch (e) { toast.error(formatApiError(e)); } })() : setPending({ kind: "user", u }));

  return (
    <div>
      <PageHeader title="Utilisateurs" description="Comptes publics, collaborateurs et administrateurs internes : rôles et statuts. Les coordonnées affichées se limitent au nom et à l'email." />
      <div className="mb-6 flex flex-col gap-2 md:flex-row">
        <form className="flex-1" onSubmit={(e) => { e.preventDefault(); setF({ ...f, q, page: 1 }); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom ou email…" data-testid="admin-users-search" />
        </form>
        <select className="field-select md:w-56" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value, page: 1 })} data-testid="admin-users-type-filter">{Object.entries(TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select className="field-select md:w-40" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value, page: 1 })} data-testid="admin-users-status-filter"><option value="">Tous statuts</option><option value="active">Actifs</option><option value="inactive">Désactivés</option></select>
      </div>
      {loading && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && (
        <>
          <div className="card-surface overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Utilisateur</th><th className="px-4 py-3">Rôles & espaces</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Inscrit le</th><th className="px-4 py-3 text-right">Action</th></tr>
              </thead>
              <tbody className="divide-y">{data.items.map((u) => <UserRow key={u.id} u={u} me={me} can={can} onToggle={onToggle} onEdit={(m, change) => setPending({ kind: "membership", m, change })} />)}</tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-end gap-2 text-sm text-slate-500">
            <span data-testid="admin-users-total">{data.total} compte(s)</span>
            <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="admin-users-prev">Précédent</Button>
            <span>{data.page} / {data.pages}</span>
            <Button variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="admin-users-next">Suivant</Button>
          </div>
        </>
      )}
      {pending && <ReasonDialog testId="admin-user-action" title={pending.kind === "user" ? `Désactiver ${pending.u.name}` : `Modifier l'accès — ${pending.m.company}`}
        label="Confirmer" variant={pending.kind === "user" ? "destructive" : "default"} placeholder="Motif de la décision (tracé dans le journal d'audit)"
        onConfirm={confirm} onClose={() => setPending(null)} />}
    </div>
  );
}
